/**
 * FunnelErrorBoundary
 *
 * In dev: shows the error visually for debugging.
 * In prod: renders nothing, keeps the rest of the page intact.
 * Resets when resetKey changes (e.g., navigation).
 */

import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { funnelLogger } from '../logger';

interface Props {
  children: ReactNode;
  resetKey?: string;
}

interface State {
  hasError: boolean;
  errorMessage: string | null;
}

export class FunnelErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, errorMessage: null });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    funnelLogger.error('[FunnelExperience] Render error caught by boundary', error, {
      componentStack: errorInfo.componentStack ?? null,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (process.env.NODE_ENV === 'development') {
        return (
          <div style={{position:'fixed',bottom:24,right:24,width:380,background:'#fef2f2',border:'2px solid #ef4444',borderRadius:12,padding:16,zIndex:9999,fontSize:13,color:'#991b1b',maxHeight:300,overflow:'auto'}}>
            <strong>FunnelExperience Crash</strong>
            <pre style={{margin:'8px 0 0',fontSize:11,whiteSpace:'pre-wrap'}}>{this.state.errorMessage}</pre>
          </div>
        );
      }
      return null;
    }
    return this.props.children;
  }
}
